import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { Icon } from "@/components/atoms/Icon";
import { icons } from "@/objects/@icons";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import { ActiveStreamingLabel } from "./ToolCallCardActiveStreamingLabel";

type CollapsibleActionRowProps = {
  header: string;
  body: string;
  isActive: boolean;
  error?: string | null;
};

/** Muted inline row with chevron + animated subtext (Thought / Explore). */
export function CollapsibleActionRow({
  header,
  body,
  isActive,
  error,
}: CollapsibleActionRowProps) {
  const hasBody = body.length > 0;
  // Closed until the user opens it, and left as the user left it: the step
  // starting or finishing is not a reason to show or take away its reasoning.
  const [isOpen, setIsOpen] = useState(false);
  const isExpanded = hasBody && isOpen;
  const canToggle = hasBody;

  return (
    <div className="mb-2">
      <div
        onClick={() => {
          if (canToggle) {
            setIsOpen((open) => !open);
          }
        }}
        className={cn(
          "flex items-start gap-0.5 w-full max-w-full -ml-1 -mr-1 pl-1 pr-1 pt-0.5 pb-0.5 rounded-sm",
          canToggle
            ? "cursor-pointer select-none [&:hover_.collapsible-action-row-chevron]:opacity-100"
            : "cursor-default select-auto",
        )}
      >
        <Typography
          className="collapsible-action-row-header text-xs [line-height:1.5] min-w-0"
          component="div"
          level="body-sm"
        >
          <ActiveStreamingLabel active={isActive}>
            {header}
          </ActiveStreamingLabel>
        </Typography>

        {hasBody && (
          <motion.div
            animate={{ rotate: isExpanded ? 0 : -90 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            className={cn(
              "collapsible-action-row-chevron flex items-center text-muted-foreground/80 shrink-0 mt-[2px] [transition:opacity_0.15s_ease]",
              // Visible at rest so a finished row reads as expandable.
              isActive || isExpanded ? "opacity-100" : "opacity-60",
            )}
          >
            <Icon ReactIcon={icons.chevronDown} size="sm" />
          </motion.div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && hasBody && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <Typography
              component="div"
              level="body-sm"
              className="text-muted-foreground/60 text-xs [line-height:1.5] whitespace-pre-wrap mt-0.5"
            >
              {body}
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <Typography level="body-sm" className="text-destructive mt-0.5">
          {error}
        </Typography>
      )}
    </div>
  );
}
